import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import { createLogger, debounce } from "@tsmono/util";

import { useProperty } from "./useProperty";

const log = createLogger("scrolling");

export function useStatefulScrollPosition<
  T extends HTMLElement = HTMLDivElement,
>(
  elementRef: RefObject<T | null>,
  elementKey: string,
  delay = 1000,
  scrollable = true
) {
  const [scrollPosition, setScrollPosition] = useProperty<number>(
    "scrollPosition",
    elementKey
  );

  // Stash reactive value in a ref so callbacks can read it without
  // triggering re-renders in the consumer component.
  const scrollPositionRef = useRef(scrollPosition);
  useEffect(() => {
    scrollPositionRef.current = scrollPosition;
  }, [scrollPosition]);

  const storePosition = useCallback(
    (position: number) => {
      log.debug(`Storing scroll position`, elementKey, position);
      setScrollPosition(position);
    },
    [elementKey, setScrollPosition]
  );

  const debouncedStore = useMemo(
    () => debounce(storePosition, delay),
    [storePosition, delay]
  );

  // Debounce the position, not the Event: the trailing tick can land after
  // unmount, when the detached element's scrollTop reads 0 — reading it at
  // fire time would clobber the saved position with 0.
  const handleScroll = useCallback(
    (e: Event) => {
      if (e.target instanceof Element) {
        debouncedStore(e.target.scrollTop);
      }
    },
    [debouncedStore]
  );

  // Function to manually restore scroll position
  const restoreScrollPosition = useCallback(() => {
    const element = elementRef.current;
    const savedPosition = scrollPositionRef.current;

    if (element && savedPosition !== undefined) {
      requestAnimationFrame(() => {
        element.scrollTop = savedPosition;

        requestAnimationFrame(() => {
          if (element.scrollTop !== savedPosition) {
            element.scrollTop = savedPosition;
          }
        });
      });
    }
  }, [elementRef]);

  // Set up scroll listener and restore position on mount
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !scrollable) {
      return;
    }
    log.debug(`Restore Scroll Hook`, elementKey);

    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    // Restore scroll position on mount
    const savedPosition = scrollPositionRef.current;
    if (savedPosition !== undefined) {
      log.debug(`Restoring scroll position`, savedPosition);

      const tryRestoreScroll = () => {
        if (element.scrollHeight > element.clientHeight) {
          if (element.scrollTop !== savedPosition) {
            element.scrollTop = savedPosition;
            log.debug(`Scroll position restored to ${savedPosition}`);
          }
          return true;
        }
        return false;
      };

      if (!tryRestoreScroll()) {
        let attempts = 0;
        const maxAttempts = 20;

        const pollForRender = () => {
          if (tryRestoreScroll() || attempts >= maxAttempts) {
            if (attempts >= maxAttempts) {
              log.debug(
                `Failed to restore scroll after ${maxAttempts} attempts`
              );
            }
            return;
          }

          attempts += 1;
          pollTimer = setTimeout(pollForRender, 100);
        };

        pollTimer = setTimeout(pollForRender, 100);
      }
    }

    element.addEventListener("scroll", handleScroll);

    return () => {
      clearTimeout(pollTimer);
      // A debounce tick pending here is left to fire after unmount —
      // deliberate: it writes the position captured at event time to the
      // app's state store (safe post-unmount), so a scroll made just before
      // unmounting is still restorable.
      element.removeEventListener("scroll", handleScroll);
    };
  }, [elementKey, elementRef, handleScroll, scrollable]);

  return { restoreScrollPosition };
}
